import React from 'react';
import styled, { css } from 'styled-components';

import Admin from 'hive-admin';
import { ShopOutlined, AppstoreOutlined } from '@ant-design/icons';

import { getI18nFields } from '../../helpers/i18n';

import {
  ADMIN as FILTERS_ADMIN,
  SELLER as FILTERS_SELLER,
} from './filters';

import {
  ADMIN as COLUMNS_ADMIN,
  SELLER as COLUMNS_SELLER,
} from './columns';

import {
  ADMIN as FIELDS_ADMIN,
  SELLER as FIELDS_SELLER,
} from './fields';

import { getCalculatedPriceIncludingVat } from './fields/variation';

import { renderCurrency } from '../../components/SystemCountry';

import Types from '../../modules/types';
import tests from '../../helpers/tests';

const {
  viewerIsAdminAndDoesNotHavePermissionToEditProducts,
} = tests;

const StockWrapper = styled.div`
  display: flex;
  /* width: 100%; */
  gap: 10px;
  align-items: center;
  justify-content: flex-end;
  font-size: 80%;
`;

const StockStorageWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 5px;
`;

const StockStoragePart = styled.div`
  height: 100%;
  ${({ $outOfStock }) => $outOfStock && css`
    opacity: 0.3;
  `}
`;

const _renderStock = (record, props) => (
  <StockWrapper>
    {
      (
        Types.PRODUCT_KIND_ABSTRACT.includes(record.kind)
        ? [Infinity]
        : props.viewer?.role === 'ADMIN'
        ? Types.SHIPPING_STORAGE_TANGIBLE
        : (
              props.viewer?.storeType === 'MERCHANT'
            ? (
                  props.viewer?.storeData?.storageOptions?.length
                ? props.viewer.storeData.storageOptions
                : [null]
              )
            : props.viewer?.storeType === 'RESELLER'
            ? ['WAREHOUSE']
            : [null]
          )
      )
      .map(storage => (
        storage === Infinity
        ? '∞'
        : !storage
        ? '-'
        : (
          <StockStorageWrapper
            key={storage}
          >
            <StockStoragePart
              $outOfStock={record.stockAggregates?.[storage] === 0}
            >
              {
                  record.stockAggregates
                ? (record.stockAggregates[storage] || 0)
                : '-'
              }
            </StockStoragePart>
            <StockStoragePart>
              {
                  storage === 'WAREHOUSE'
                ? (
                    <AppstoreOutlined
                      title={Types.SHIPPING_STORAGE_LABELS_MAP[storage]}
                    />
                  )
                : storage === 'LOCAL'
                ? (
                    <ShopOutlined
                      title={Types.SHIPPING_STORAGE_LABELS_MAP[storage]}
                    />
                  )
                : null
              }
            </StockStoragePart>
          </StockStorageWrapper>
        )
      ))
    }
  </StockWrapper>
)

export default [
  ['ADMIN', {
    path: '/products',
  }],
  ['SELLER', {
    path: '/store/products',
    archiveLoadUrl: '/products',
  }],
].reduce(
  (pages, [forRole, config]) => {
    const [
      COLUMNS,
      FILTERS,
      FIELDS,
    ] = (
        forRole === 'ADMIN'
      ? [COLUMNS_ADMIN, FILTERS_ADMIN, FIELDS_ADMIN]
      : forRole === 'SELLER'
      ? [COLUMNS_SELLER, FILTERS_SELLER, FIELDS_SELLER]
      : []
    );
    pages.push(...Admin.compileFromLibrary(['GroupResource', {
      title: 'Products',
      // icon: 'barcode',
      afterArchivePages: [
        ['PageArchiveTable', {
          label: 'Products',
          title: 'Products',
          // icon: 'exclamation-circle',
          getUseFiltersExtra: () => true,
          // titleColumnPath: '_id',
          path: `${config.path}/table`,
          alias: config.path,
          hidden: [() => true],
          // alias: config.path,
          exact: true,
          loadUrl: '/products',
          createButtonPath: `${config.path}/create`,
          columns: COLUMNS,
          filters: FILTERS,
          loadExtractData: response => (
            response
            ? { count: response.data.count, data: response.data.data }
            : { count: 0, data: [] }
          ),
        }],
      ],
      archiveComponent: 'PageArchiveGrid',
      archiveConfig: {
        getUseFiltersExtra: () => true,
        title: 'Products',
        label: 'Products',
        // icon: 'barcode',
        hidden: [
          [
            'condition.checkViewer',
            'role', '!==', forRole,
          ],
        ],
        createButtonPath: `${config.path}/create`,
        gridItemLink: `${config.path}/:_id`,
        gridItemHeaderImagePath: 'image.src',
        renderGridItemContent: (item, index, props) => {
          const isAdmin = props?.viewer?.role === 'ADMIN';
          let reviewSupport = false;
          let isUsingVat = false;
          if (isAdmin && item.store?.trn?.active) {
            isUsingVat = true;
          } else if (
            props.viewer.storeData?.trn?.active
          ) {
            isUsingVat = true;
          }
          if (isAdmin && item.store?.reviewSupported && item.rating >= 1) {
            reviewSupport = true;
          } else if (
            props.viewer.storeData?.reviewSupported && item.rating >= 1
          ) {
            reviewSupport = true;
          }

          const upcoming = new Date(item.publishedAt) > Date.now();

          return (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              height: '100%',
              opacity: upcoming ? 0.5 : 1,
            }}
            >
              <div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <div style={{
                    width: reviewSupport ? '85%' : '100%',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  >
                    {item.name}
                  </div>
                  {
                    reviewSupport
                    ? (
                      <div
                        style={{
                          fontSize: '80%',
                        }}
                      >
                        {Types.decimalize(item.rating, '.', 1)}
                        {' '}
                        ☆
                      </div>
                    )
                    : null
                  }
                </div>
                <div style={{ fontSize: '80%' }}>
                  {
                    !!isAdmin && (
                      <>
                        <span>
                          {item.store?.name}
                        </span>
                        <span> / </span>
                      </>
                    )
                  }
                  {
                    item.category?.name && (
                      <span>{item.category?.name}</span>
                    )
                  }
                  {
                    !!item.subcategoryData?.name && (
                      <span>
                        {' '}
                        /
                        {' '}
                        {item.subcategoryData?.name}
                      </span>
                    )
                  }
                </div>
              </div>
              <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: '15px',
                }}
              >
                <div className="tags">
                  <div className="tag">
                    {renderCurrency()}
                    {' '}
                    {Types.decimalize(
                      isUsingVat
                      ? getCalculatedPriceIncludingVat(
                        item.priceLowest,
                        { systemCountry: item.systemCountry },
                      )
                      : item.priceLowest,
                    )}
                  </div>
                </div>
                {
                  upcoming || item.featured
                  ? (
                    <div className="tags">
                      <div
                        className="tag"
                      >
                        {
                          upcoming
                          ? 'Upcoming'
                          : 'Featured'
                        }
                      </div>
                    </div>
                  )
                  : null
                }
                {/* {renderStock(item, props)} */}
              </div>
            </div>
          )
        },
      },
      singleLoadUrl: `/products/:id/?${
        [
          'includeStoreData=true',
          'includeStoreSlug=true',
          'includeStoreStorageOptions=true',
        ].join('&')
      }`,
      singleConfig: {
        hidden: true,
        alias: config.path,
        backButtonPaths: [config.path, `${config.path}/table`],
        duplicateLoadUrl: '/products/:duplicate',
        // eslint-disable-next-line arrow-body-style
        getPageFields: getI18nFields,
      },
      singleEditConfig: {
        actions: [
          ['ActionSaveAndReload', {
              skip: [
                // eslint-disable-next-line max-len
                // viewerIsAdminAndDoesNotHavePermissionToEditProducts('forceProductsEditAndDeletion'),
              ],
            },
          ],
          ['ActionDelete', {
            skip: [
              // eslint-disable-next-line max-len
              // viewerIsAdminAndDoesNotHavePermissionToEditProducts('forceProductsEditAndDeletion'),
            ],
          }],
          ['Action', {
            name: 'duplicate',
            title: 'Duplicate',
            ghost: true,
            onClick: (props) => props.history.push(
              `${config.path}/create?duplicate=${
                props.data ? props.data._id : null
              }`
            ),
          }],
        ],
      },
      singleCreateConfig: {
        headerTitle: 'New Product',
      },
      redirect: [
        ['redirect.unauthorized'],
        [
          'redirect.checkViewer',
          'role', '!==', forRole,
            forRole === 'ADMIN'
          ? '/store/products'
          : forRole === 'SELLER'
          ? '/products'
          : null,
        ],
      ],
      columns: COLUMNS,
      filters: FILTERS,
      fields: FIELDS,
      ...config,
    }]).pages);
    return pages;
  },
  [],
);
